import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  App,
  CreateAppRequest,
  UpdateAppRequest,
  FileItem,
  InitiateUploadRequest,
  InitiateUploadResponse,
  ChunkUploadRequest,
  ChunkUploadResponse,
  CompleteUploadRequest,
  AbortUploadRequest,
  DownloadResponse,
  DownloadRequest,
  PublicApp,
  ApiError,
  Folder,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse
} from '../types/api';

// App structure type for the new structure endpoint
export interface AppStructure {
  folders: (Folder & { folders: Folder[]; files: FileItem[] })[];
  files: FileItem[];
}

export class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - just remove token
          // Let the auth store and router handle the redirect
          localStorage.removeItem('auth_token');
        }
        return Promise.reject(error);
      }
    );
  }

  // Helper method to handle API responses
  private handleResponse<T>(response: AxiosResponse<T>): T {
    return response.data;
  }

  private handleError(error: any): never {
    const apiError: ApiError = {
      error: error.response?.data?.error || error.message || 'An error occurred',
      status: error.response?.status
    };
    throw apiError;
  }

  // ============================================================================
  // AUTHENTICATION API
  // ============================================================================

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await this.api.post<AuthResponse>('/auth/register', userData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.api.post<AuthResponse>('/auth/login', credentials);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getCurrentUser(): Promise<{ user: { id: string; email: string; name: string | null } }> {
    try {
      const response = await this.api.get<{ user: { id: string; email: string; name: string | null } }>('/auth/me');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async forgotPassword(emailData: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    try {
      const response = await this.api.post<ForgotPasswordResponse>('/auth/forgot-password', emailData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async resetPassword(resetData: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    try {
      const response = await this.api.post<ResetPasswordResponse>('/auth/reset-password', resetData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // APPS API
  // ============================================================================

  async getApps(): Promise<App[]> {
    try {
      const response = await this.api.get<App[]>('/apps');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createApp(appData: CreateAppRequest): Promise<App> {
    try {
      const response = await this.api.post<App>('/apps', appData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateApp(appId: string, updates: UpdateAppRequest): Promise<App> {
    try {
      const response = await this.api.patch<App>(`/apps/${appId}`, updates);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteApp(appId: string): Promise<{ message: string }> {
    try {
      const response = await this.api.delete<{ message: string }>(`/apps/${appId}`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // APP STRUCTURE API (NEW - replaces separate file/folder listing)
  // ============================================================================

  async getAppStructure(appId: string): Promise<AppStructure> {
    try {
      const response = await this.api.get<AppStructure>(`/files/${appId}/structure`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // FOLDER MANAGEMENT API (UPDATED - uses folderId instead of paths)
  // ============================================================================

  async createFolder(appId: string, folderData: { name: string; parentId?: string }): Promise<Folder> {
    try {
      const response = await this.api.post<Folder>(`/files/${appId}/folders`, folderData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async renameFolder(appId: string, folderId: string, name: string): Promise<Folder> {
    try {
      const response = await this.api.patch<Folder>(`/files/${appId}/folders/${folderId}`, { name });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteFolder(appId: string, folderId: string): Promise<{ message: string }> {
    try {
      const response = await this.api.delete<{ message: string }>(`/files/${appId}/folders/${folderId}`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // FILE MANAGEMENT API (UPDATED - uses dedicated file endpoints)
  // ============================================================================

  async renameFile(appId: string, fileId: string, name: string): Promise<FileItem> {
    try {
      const response = await this.api.patch<FileItem>(`/files/${appId}/files/${fileId}`, { name });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteFile(appId: string, fileId: string): Promise<{ message: string }> {
    try {
      const response = await this.api.delete<{ message: string }>(`/files/${appId}/files/${fileId}`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // UPLOAD API (unchanged from backend perspective)
  // ============================================================================

  async initiateUpload(appId: string, uploadData: InitiateUploadRequest): Promise<InitiateUploadResponse> {
    try {
      const response = await this.api.post<InitiateUploadResponse>(`/files/${appId}/initiate-upload`, uploadData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getChunkUploadUrl(appId: string, chunkData: ChunkUploadRequest): Promise<ChunkUploadResponse> {
    try {
      const response = await this.api.post<ChunkUploadResponse>(`/files/${appId}/chunk-url`, chunkData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async completeUpload(appId: string, completeData: CompleteUploadRequest): Promise<FileItem> {
    try {
      const response = await this.api.post<FileItem>(`/files/${appId}/complete-upload`, completeData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async abortUpload(appId: string, abortData: AbortUploadRequest): Promise<{ message: string }> {
    try {
      const response = await this.api.post<{ message: string }>(`/files/${appId}/abort-upload`, abortData);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // DOWNLOAD API (unchanged)
  // ============================================================================

  async getDownloadUrl(appId: string, fileId: string, params?: DownloadRequest): Promise<DownloadResponse> {
    try {
      const response = await this.api.get<DownloadResponse>(`/files/${appId}/${fileId}/download`, { params });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // PUBLIC API (SIMPLIFIED - only 3 endpoints now)
  // ============================================================================

  async getPublicApp(id: string): Promise<PublicApp> {
    try {
      const response = await this.api.get<PublicApp>(`/public/app/${id}`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPublicFileDownloadUrl(id: string, fileId: string, params?: DownloadRequest): Promise<DownloadResponse> {
    try {
      const response = await this.api.get<DownloadResponse>(`/public/app/${id}/files/${fileId}/download`, { params });
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPublicAppStructure(id: string): Promise<AppStructure> {
    try {
      const response = await this.api.get<AppStructure>(`/public/app/${id}/structure`);
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // UTILITY METHODS (unchanged)
  // ============================================================================

  async uploadChunk(url: string, chunk: Blob): Promise<string> {
    try {
      const response = await axios.put(url, chunk, {
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      });
      return response.headers.etag || response.headers.ETag || '';
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async downloadFile(url: string, headers?: Record<string, string>): Promise<Blob> {
    try {
      const response = await axios.get(url, {
        responseType: 'blob',
        headers
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await this.api.get<{ status: string; timestamp: string }>('/health');
      return this.handleResponse(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// Export a singleton instance
export const apiService = new ApiService(); 